import React, { useCallback, useEffect } from 'react';
import { ErrorMessage } from '@hookform/error-message';
import { Controller, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { FaRegTrashAlt } from 'react-icons/fa';
import { Col, Form, Input, Label, Row } from 'reactstrap';

import ReactButton from '../../../../components/Buttons/ReactButton';
import FlexSelect from '../../../../components/Inputs/FlexSelect';
import ToggleSwitch from '../../../../components/ToggleSwitch/ToggleSwitch';
import { useGetTeams } from '../../../User/form/hooks/useGetTeams';
import FormTooltip from 'components/Inputs/FormTooltip';

import { AddQuestionButton, RemoveQuestionButton } from './styled';

const QUESTION_WEIGHT = [
  { value: 1 },
  { value: 2 },
  { value: 3 },
  { value: 4 },
  { value: 5 },
  { value: 'Crítico' }
];

export default function PaperForm({
  data,
  onSubmit,
  onCancel,
  onDeleteQuestion,
  onDeleteQuestionGroup,
  editMode = false
}) {
  const { teams = [] } = useGetTeams();
  const { reset, control, handleSubmit, errors, setValue, clearErrors } =
    useForm({
      defaultValues: {
        name: '',
        type: 'basic',
        teams: [],
        questionGroups: [],
        questions: []
      }
    });

  const watchedCategories =
    useWatch({
      control,
      name: 'categories'
    }) || [];

  const {
    fields: questionFields,
    append: appendQuestion,
    remove: removeQuestion
  } = useFieldArray({
    control,
    name: 'questions'
  });

  const {
    fields: categoryFields,
    append: appendCategory,
    remove: removeCategory
  } = useFieldArray({
    control,
    name: 'categories'
  });

  const {
    fields: questionGroupFields,
    append: appendQuestionGroup,
    remove: removeQuestionGroup
  } = useFieldArray({
    control,
    name: 'questionGroups'
  });

  const customSetValue = useCallback(
    (fieldName, value) => {
      setValue(fieldName, value, { shouldValidate: true, shouldDirty: true });
      clearErrors(fieldName);
    },
    [setValue, clearErrors]
  );

  useEffect(() => {
    if (data) {
      const questionGroupsData = (data.questionGroups || [])
        .reduce((acc, group) => {
          const existing = acc.find((g) => g.name === group.name);
          if (!existing) {
            acc.push({
              tempId: group.id,
              name: group.name,
              enabled: group.enabled,
              new: false
            });
          } else if (group.id < existing.tempId) {
            const index = acc.findIndex((g) => g.name === group.name);
            acc[index] = {
              tempId: group.id,
              name: group.name,
              enabled: group.enabled,
              new: false
            };
          }
          return acc;
        }, [])
        .sort((a, b) => a.tempId - b.tempId);

      const allRawQuestions = data.questions || [];
      const groupedRawQuestions = allRawQuestions.filter(
        (q) => q.group_id != null
      );
      const looseRawQuestions = allRawQuestions.filter(
        (q) => q.group_id == null
      );

      const groupedQuestions = groupedRawQuestions.map((question) => ({
        question_id: question.id,
        description: question.description,
        weightage:
          question.type === 'CRITICAL' ? 'Crítico' : question.weightage,
        enabled: question.enabled,
        required: question.required,
        new: false,
        groupId: question.group_id
      }));

      let looseQuestionsData = looseRawQuestions.map((question) => ({
        question_id: question.id,
        description: question.description,
        weightage:
          question.type === 'CRITICAL' ? 'Crítico' : question.weightage,
        enabled: question.enabled,
        required: question.required,
        new: false,
        groupId: null
      }));

      looseQuestionsData.sort((a, b) => a.question_id - b.question_id);

      if (looseQuestionsData.length > 0 && questionGroupsData.length > 0) {
        const questionsPerGroup = Math.floor(
          looseQuestionsData.length / questionGroupsData.length
        );

        looseQuestionsData.forEach((q, qIdx) => {
          const groupIdx = Math.floor(qIdx / questionsPerGroup);
          const groupId =
            questionGroupsData[groupIdx]?.tempId ||
            questionGroupsData[0].tempId;
          q.groupId = groupId;
        });
      }

      const allQuestionsData = [...groupedQuestions, ...looseQuestionsData];

      reset({
        name: data.name || '',
        description: data.description || '',
        type: data.type || 'basic',
        teams: data.teams?.map((team) => team.id) || [],
        categories: (data.categories || []).map((category) => ({
          category_id: category.id,
          name: category.name,
          enabled: category.enabled,
          new: false
        })),
        questions: allQuestionsData,
        questionGroups: questionGroupsData
      });
    }
  }, [data, reset]);

  const errorFormMessage = (message) => (
    <p className="text-danger">{message}</p>
  );

  const addQuestionToGroup = useCallback(
    (groupIndex) => {
      const groupData = questionGroupFields[groupIndex];
      const groupId = groupData?.tempId || `group_${Date.now()}_${groupIndex}`;
      appendQuestion({
        description: '',
        weightage: null,
        required: true,
        enabled: true,
        new: true,
        groupId
      });
    },
    [appendQuestion, questionGroupFields]
  );

  const handleRemoveGroup = async (groupIndex) => {
    const groupData = questionGroupFields[groupIndex];
    const currentGroupId = groupData?.tempId || `group_${groupIndex}`;
    const isNewGroup = groupData?.new || false;

    const groupQuestionIndices = questionFields
      .map((_, globalIndex) => globalIndex)
      .filter(
        (globalIndex) =>
          String(questionFields[globalIndex]?.groupId) ===
          String(currentGroupId)
      );

    const questionsToDelete = [];
    groupQuestionIndices.forEach((globalIndex) => {
      const questionValue = questionFields[globalIndex];
      if (
        onDeleteQuestion &&
        editMode &&
        questionValue?.question_id &&
        typeof questionValue.question_id === 'number'
      ) {
        questionsToDelete.push(questionValue.question_id);
      }
    });
    for (let i = groupQuestionIndices.length - 1; i >= 0; i--) {
      const globalIndex = groupQuestionIndices[i];
      removeQuestion(globalIndex);
    }
    if (questionsToDelete.length > 0) {
      try {
        await Promise.all(
          questionsToDelete.map(async (questionId) => {
            await onDeleteQuestion(questionId);
          })
        );
      } catch (err) {
        console.error('Erro ao deletar questões no backend:', err);
      }
    }

    removeQuestionGroup(groupIndex);

    if (
      onDeleteQuestionGroup &&
      !isNewGroup &&
      typeof groupData?.tempId === 'number'
    ) {
      try {
        await onDeleteQuestionGroup(groupData.tempId);
      } catch (err) {
        console.error(`Erro ao deletar grupo ${groupData.tempId}:`, err);
      }
    }

    setTimeout(() => {}, 0);
  };

  const onSubmitForm = (data) => {
    const questions = data.questions.map((q) => {
      if (q.weightage === 'Crítico') {
        return {
          ...q,
          weightage: 1,
          type: 'CRITICAL'
        };
      }
      return {
        ...q
      };
    });

    onSubmit({ ...data, questions });
  };

  return (
    <Form className="needs-validation" onSubmit={handleSubmit(onSubmitForm)}>
      <h4 className="mb-0">Gerais</h4>
      <hr className="mt-2 mb-4" />

      <Row className="mb-3">
        <Col md="6">
          <label className="form-control-label">Nome da monitoria*</label>
          <Controller
            as={Input}
            control={control}
            name="name"
            rules={{ required: 'Nome da monitoria é um campo obrigatório' }}
          />
          <ErrorMessage
            errors={errors}
            name="name"
            render={({ message }) => errorFormMessage(message)}
          />
        </Col>
        <Col md="6">
          <label className="form-control-label">Equipe*</label>
          <Controller
            render={(props) => (
              <FlexSelect
                dataOptions={teams}
                value={props.value}
                valueController={customSetValue}
                fieldName="teams"
                labelName="name"
                valueName="id"
                isMulti
              />
            )}
            control={control}
            name="teams"
            rules={{ required: 'Selecione uma equipe' }}
          />
          <ErrorMessage
            errors={errors}
            name="teams"
            render={({ message }) => errorFormMessage(message)}
          />
        </Col>
      </Row>

      <Row className="my-4">
        <Col md="6">
          <div className="d-flex">
            <label className="form-control-label">Tipo de nota*:</label>
            <div className="ml-5">
              <Controller
                name="type"
                control={control}
                render={(props) => (
                  <Input
                    {...props}
                    id="type-basic"
                    disabled={editMode}
                    type="radio"
                    value="basic"
                  />
                )}
                rules={{ required: 'Selecione um tipo de nota' }}
              />
              <Label htmlFor="type-basic">de 1 a 5</Label>
            </div>
            <div className="ml-5">
              <Controller
                name="type"
                control={control}
                render={(props) => (
                  <Input
                    {...props}
                    id="type-advanced"
                    disabled={editMode}
                    type="radio"
                    value="advanced"
                  />
                )}
                rules={{ required: 'Selecione um tipo de nota' }}
              />
              <Label htmlFor="type-advanced">de 1 a 10</Label>
            </div>
          </div>
          <ErrorMessage
            errors={errors}
            name="type"
            render={({ message }) => errorFormMessage(message)}
          />
        </Col>
      </Row>

      <Row style={{ marginLeft: '0.1rem' }}>
        <h4>Questões</h4>
        <FormTooltip text='As questões de peso crítico serão respondidas com "Sim" ou "Não". Caso uma questão de peso crítico for marcada como "Sim", a nota do formulário será zerada.' />
      </Row>
      <hr className="mt-2 mb-4" />

      {questionGroupFields.map((groupField, groupIndex) => {
        const groupData = questionGroupFields[groupIndex];
        const currentGroupId = groupData?.tempId || `group_${groupIndex}`;

        const groupQuestionIndices = questionFields
          .map((_, globalIndex) => globalIndex)
          .filter(
            (globalIndex) =>
              String(questionFields[globalIndex]?.groupId) ===
              String(currentGroupId)
          );

        return (
          <div
            key={groupField.id}
            className="mb-4 p-3"
            style={{
              backgroundColor: '#f8f9fa',
              border: '1px solid #dee2e6',
              borderRadius: '8px'
            }}
          >
            <Row className="mb-3 align-items-center">
              <Col md="8">
                <Controller
                  as={Input}
                  control={control}
                  name={`questionGroups.${groupIndex}.name`}
                  rules={{ required: 'Nome da categoria é obrigatório' }}
                  defaultValue={groupData?.name || ''}
                  placeholder="Nome da categoria"
                  className="mb-0"
                />
                <Controller
                  as={Input}
                  control={control}
                  name={`questionGroups.${groupIndex}.tempId`}
                  hidden
                  defaultValue={groupData?.tempId || ''}
                />
                <ErrorMessage
                  errors={errors}
                  name={`questionGroups.${groupIndex}.name`}
                  render={({ message }) => errorFormMessage(message)}
                />
              </Col>
              <Col md="2">
                {editMode ? (
                  <>
                    <label className="form-control-label">Habilitado</label>
                    <Controller
                      render={(props) => (
                        <ToggleSwitch
                          checked={props.value ?? true}
                          id={`questionGroups.${groupIndex}.enabled`}
                          onChange={() =>
                            customSetValue(
                              `questionGroups.${groupIndex}.enabled`,
                              !(props.value ?? true)
                            )
                          }
                        />
                      )}
                      control={control}
                      name={`questionGroups.${groupIndex}.enabled`}
                      defaultValue={groupData?.enabled ?? true}
                    />
                  </>
                ) : null}
              </Col>
              <Col md="2" className="d-flex justify-content-end">
                <RemoveQuestionButton
                  type="button"
                  onClick={() => handleRemoveGroup(groupIndex)}
                  disabled={questionGroupFields.length <= 1}
                  style={{
                    opacity: questionGroupFields.length <= 1 ? 0.5 : 1,
                    cursor:
                      questionGroupFields.length <= 1
                        ? 'not-allowed'
                        : 'pointer',
                    pointerEvents:
                      questionGroupFields.length <= 1 ? 'none' : 'auto'
                  }}
                  title={
                    questionGroupFields.length <= 1
                      ? 'É necessário manter pelo menos um grupo de questões'
                      : 'Remover grupo'
                  }
                >
                  <FaRegTrashAlt size={20} />
                </RemoveQuestionButton>
              </Col>
            </Row>

            <h6 className="text-muted mb-3">Perguntas da categoria:</h6>

            {groupQuestionIndices.map((globalIndex, localIndex) => {
              const questionValue = questionFields[globalIndex];
              const questionField = questionFields[globalIndex];

              return (
                <Row key={questionField.id} className="mb-3 ml-3">
                  <Col md="6">
                    <div className="d-flex align-items-center">
                      <span className="mr-3 font-weight-bold text-primary">
                        Pergunta {localIndex + 1}:
                      </span>
                      <Controller
                        as={Input}
                        control={control}
                        name={`questions.${globalIndex}.question_id`}
                        defaultValue={questionValue?.question_id}
                        hidden
                      />
                      <Controller
                        as={Input}
                        control={control}
                        name={`questions.${globalIndex}.groupId`}
                        hidden
                        defaultValue={questionValue?.groupId || ''}
                      />
                      <div className="flex-grow-1">
                        <Controller
                          as={Input}
                          control={control}
                          name={`questions.${globalIndex}.description`}
                          rules={{
                            required: 'Descrição da pergunta é obrigatória'
                          }}
                          defaultValue={questionValue?.description || ''}
                          placeholder="Descrição da pergunta"
                        />
                        <ErrorMessage
                          errors={errors}
                          name={`questions.${globalIndex}.description`}
                          render={({ message }) => errorFormMessage(message)}
                        />
                      </div>
                    </div>
                  </Col>
                  <Col md="2">
                    <Controller
                      render={(props) => {
                        const currentValue = props.value || null;
                        return (
                          <FlexSelect
                            dataOptions={QUESTION_WEIGHT.filter((option) => {
                              if (questionValue?.new) return true;
                              if (
                                option.value == 'Crítico' &&
                                currentValue == 'Crítico'
                              )
                                return true;
                              if (
                                option.value != 'Crítico' &&
                                currentValue != 'Crítico'
                              )
                                return true;
                              return false;
                            })}
                            closeMenuOnSelect={true}
                            value={props.value}
                            valueController={customSetValue}
                            fieldName={props.name}
                            placeholder="Peso"
                            labelName="value"
                            valueName="value"
                          />
                        );
                      }}
                      control={control}
                      name={`questions.${globalIndex}.weightage`}
                      rules={{ required: 'Selecione um peso' }}
                      defaultValue={questionValue?.weightage}
                    />
                    <ErrorMessage
                      errors={errors}
                      name={`questions.${globalIndex}.weightage`}
                      render={({ message }) => errorFormMessage(message)}
                    />
                  </Col>
                  <Col
                    md="3"
                    className="d-flex align-items-center justify-content-between"
                  >
                    <div className="d-flex align-items-center mr-3">
                      <label className="form-control-label mb-0 mr-2">
                        Obrigatório
                      </label>
                      <Controller
                        render={(props) => (
                          <ToggleSwitch
                            checked={props.value ?? true}
                            id={`questions.${globalIndex}.required`}
                            onChange={() =>
                              customSetValue(props.name, !(props.value ?? true))
                            }
                          />
                        )}
                        control={control}
                        name={`questions.${globalIndex}.required`}
                        defaultValue={questionValue?.required ?? true}
                      />
                    </div>
                    {editMode ? (
                      <div className="d-flex align-items-center">
                        <label className="form-control-label mb-0 mr-2">
                          Habilitada
                        </label>
                        <Controller
                          render={(props) => (
                            <ToggleSwitch
                              checked={props.value ?? true}
                              id={`questions.${globalIndex}.enabled`}
                              onChange={() =>
                                customSetValue(
                                  props.name,
                                  !(props.value ?? true)
                                )
                              }
                            />
                          )}
                          control={control}
                          name={`questions.${globalIndex}.enabled`}
                          defaultValue={questionValue?.enabled ?? true}
                        />

                        <RemoveQuestionButton
                          type="button"
                          size="sm"
                          onClick={() => {
                            removeQuestion(globalIndex);
                            if (questionValue?.question_id) {
                              onDeleteQuestion(questionValue.question_id);
                            }
                          }}
                          disabled={groupQuestionIndices.length <= 1}
                          style={{
                            opacity: groupQuestionIndices.length <= 1 ? 0.5 : 1,
                            cursor:
                              groupQuestionIndices.length <= 1
                                ? 'not-allowed'
                                : 'pointer',
                            pointerEvents:
                              groupQuestionIndices.length <= 1 ? 'none' : 'auto'
                          }}
                          title={
                            groupQuestionIndices.length <= 1
                              ? 'É necessário manter pelo menos uma pergunta neste grupo'
                              : 'Remover pergunta'
                          }
                          className="ml-2"
                        >
                          <FaRegTrashAlt size={16} />
                        </RemoveQuestionButton>
                      </div>
                    ) : (
                      <RemoveQuestionButton
                        type="button"
                        size="sm"
                        onClick={() => {
                          removeQuestion(globalIndex);
                          if (questionValue?.question_id) {
                            onDeleteQuestion(questionValue.question_id);
                          }
                        }}
                        disabled={groupQuestionIndices.length <= 1}
                        style={{
                          opacity: groupQuestionIndices.length <= 1 ? 0.5 : 1,
                          cursor:
                            groupQuestionIndices.length <= 1
                              ? 'not-allowed'
                              : 'pointer',
                          pointerEvents:
                            groupQuestionIndices.length <= 1 ? 'none' : 'auto'
                        }}
                        title={
                          groupQuestionIndices.length <= 1
                            ? 'É necessário manter pelo menos uma pergunta neste grupo'
                            : 'Remover pergunta'
                        }
                      >
                        <FaRegTrashAlt size={16} />
                      </RemoveQuestionButton>
                    )}
                  </Col>
                </Row>
              );
            })}

            <Row className="ml-3">
              <Col md="12">
                <AddQuestionButton
                  type="button"
                  size="sm"
                  onClick={() => addQuestionToGroup(groupIndex)}
                >
                  + Adicionar pergunta
                </AddQuestionButton>
              </Col>
            </Row>
          </div>
        );
      })}

      <Row className="mb-4">
        <Col md="12">
          <AddQuestionButton
            type="button"
            onClick={() =>
              appendQuestionGroup({
                name: '',
                enabled: true,
                new: true,
                tempId: `group_${Date.now()}`
              })
            }
          >
            + Adicionar grupo de perguntas
          </AddQuestionButton>
        </Col>
      </Row>

      <h4 className="mt-0 mb-0" style={{ paddingTop: '2rem' }}>
        Categorias de resposta
      </h4>
      <hr className="mt-2 mb-4" />

      {categoryFields.map((category, index) => {
        const categoryData = watchedCategories[index];
        return (
          <Row key={category.id} className="mb-3">
            <Col className="col">
              <Controller
                as={Input}
                control={control}
                name={`categories.${index}.category_id`}
                hidden
                disabled
              />
              <Controller
                as={Input}
                control={control}
                name={`categories.${index}.name`}
                rules={{ required: 'O nome da categoria é obrigatório' }}
                placeholder="Nome"
              />
              <ErrorMessage
                errors={errors}
                name={`categories.${index}.name`}
                render={({ message }) => errorFormMessage(message)}
              />
            </Col>
            {editMode && !categoryData?.new ? (
              <Col className="col-auto">
                <label className="form-control-label">Habilitada</label>
                <Controller
                  render={(props) => (
                    <ToggleSwitch
                      checked={props.value ?? true}
                      id={`categories.${index}.enabled`}
                      onChange={() =>
                        customSetValue(props.name, !(props.value ?? true))
                      }
                    />
                  )}
                  control={control}
                  name={`categories.${index}.enabled`}
                />
              </Col>
            ) : (
              <Col className="col-auto d-flex">
                <RemoveQuestionButton
                  type="button"
                  onClick={() => removeCategory(index)}
                >
                  <FaRegTrashAlt size={20} />
                </RemoveQuestionButton>
              </Col>
            )}
          </Row>
        );
      })}

      <Row>
        <Col md="12">
          <AddQuestionButton
            type="button"
            onClick={() =>
              appendCategory({
                name: '',
                new: true
              })
            }
          >
            + Adicionar categoria
          </AddQuestionButton>
        </Col>
      </Row>

      <hr />
      <ReactButton btnColor="confirmation" type="submit">
        Salvar
      </ReactButton>
      <ReactButton btnColor="cancelation" onClick={onCancel}>
        Cancelar
      </ReactButton>
    </Form>
  );
}
